<template>
  <ActivityPropertyPage v-if="(property as any)?.id || (data as any)?.id" :key="(data as any)?.id" />
</template>

<script lang="ts" setup>
import { useActivityPropertyStore } from '~/store/activityProperty'
import { useRoute} from "vue-router";
import { useBreadcrumbSchema } from '~/composables/useSeo'

const runtimeConfig = useRuntimeConfig()
import ActivityPropertyPage from "~/components/ActivityPropertyPage/ActivityPropertyPage.vue";
const propertyStore = useActivityPropertyStore();
const route = useRoute();

// Google Things to Do substitutes ?currency= with the currency it showed the user. Adopt it
// BEFORE useApi(), not merely before useAsyncData: useApi() resolves its own useCookie ref the
// moment it is called and reuses that ref for every request, so a currency adopted afterwards
// never reaches the X-TRIPENING-CURRENCY header. Setting it later produced a response that
// carried Set-Cookie: currency=EUR alongside HTML priced in dollars.
useLandingCurrency()

const api = useApi();
const slug = <string>route.params.activitiesSlug;
const citySlug = <string>route.params.city;
const propertyId = Number(slug.split('-')[0])

const property = computed(() => propertyStore.property)

const data = ref<any>(null)
const { data: fetchedData } = await useAsyncData(`get-property-${propertyId}`, fetchData)

if (fetchedData.value) {
  data.value = fetchedData.value
}

// Redirect to canonical URL AFTER data is set.
// Must be outside fetchData() because navigateTo() can throw during SSR,
// which would cause fetchData to return null and lose the activity data.
if (data.value?.id) {
  await redirect(data.value.id, data.value.geo_place?.slug, data.value.slug)
}

async function redirect(id: number, cSlug: string, activitySlug: string) {
  const trueSlug = `${id}-${activitySlug}`
  const url = `/experiences/${(cSlug && cSlug !== 'null') ? cSlug : 'results'}/${trueSlug}`
  const cityCheck = !(citySlug === 'results' || citySlug === cSlug)

  if (route.params.activitiesSlug !== trueSlug || cityCheck) {
    await navigateTo({ path: url, query: route.query }, { redirectCode: 301 })
  }
}

async function fetchData() {
  try {
    const _data: any = await api(`/api/product/v3/activities/${propertyId}`)
    if (_data?.id) {
      propertyStore.setProperty(_data)
    }
    return _data
  } catch (error) {
    return null
  }
}

const { $siteConfig } = useNuxtApp()

// Breadcrumb data for JSON-LD
const baseUrl = runtimeConfig.public.baseURL
const breadcrumbs = computed(() => {
  const items: Array<{label: string, url?: string}> = [
    { label: 'Home', url: '/' },
  ]

  const countryName = data.value?.geo_place?.country_name
  const countryCode = data.value?.geo_place?.country_code
  if (countryName && countryCode) {
    items.push({ label: countryName, url: `/hotels/destinations/${countryCode.toLowerCase()}` })
  }

  items.push({ label: 'Experiences', url: '/experiences' })
  const geoSlug = data.value?.geo_place?.slug
  items.push({ label: data.value?.geo_place?.name || '', url: `/experiences/${(geoSlug && geoSlug !== 'null') ? geoSlug : citySlug}` })
  items.push({ label: data.value?.name || '' })
  return items
})

// SSR-friendly SEO meta
const pageTitle = computed(() => {
  const activityName = data.value?.name || property.value?.name || ''
  const cityName = data.value?.geo_place?.name || ''
  const brand = $siteConfig?.value?.brand || 'Tripening'
  return activityName
    ? `${activityName}${cityName ? ` in ${cityName}` : ''} | ${brand}`
    : `Discover Experiences | ${brand}`
})

const pageDescription = computed(() => {
  const activityName = data.value?.name || property.value?.name || ''
  const description = data.value?.short_description || data.value?.description || ''
  const brand = $siteConfig?.value?.brand || 'Tripening'
  if (description) {
    return description.substring(0, 160) + (description.length > 160 ? '...' : '')
  }
  return `Book ${activityName} with ${brand}. Read reviews, compare prices, and get the best experience.`
})

const canonicalUrl = computed(() => {
  const geoSlug = data.value?.geo_place?.slug
  const cSlug = (geoSlug && geoSlug !== 'null') ? geoSlug : citySlug
  const aSlug = data.value?.id ? `${data.value.id}-${data.value.slug}` : slug
  return `${runtimeConfig.public.baseURL}/experiences/${cSlug}/${aSlug}`
})

useJsonld(() => useBreadcrumbSchema(breadcrumbs.value, baseUrl))

useHead({
  title: pageTitle,
  link: [
    { rel: 'canonical', href: canonicalUrl }
  ],
  meta: [
    { name: 'description', content: pageDescription },
    { property: 'og:title', content: pageTitle },
    { property: 'og:description', content: pageDescription },
    { property: 'og:url', content: canonicalUrl },
    { property: 'og:image', content: computed(() => data.value?.cover_image_url || `${runtimeConfig.public.baseURL}/images/og-default.jpg`) },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: computed(() => $siteConfig?.value?.brand || 'Tripening') },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: pageTitle },
    { name: 'twitter:description', content: pageDescription }
  ]
})

</script>
